import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Clock } from 'lucide-react'

import { FbModal as Modal } from '../../components/UI/Modal'
import { DatePicker } from '../../components/FormUtils/DatePicker'
import TimePicker from '../../components/FormUtils/TimePicker'
import { getApiErrorDetail } from '../../utils/getApiErrorDetail'
import { useScheduleCampaignLaunch } from './campaign_status_api'

interface Props {
  show: boolean
  onHide: () => void
  bisonCampaignId: number
  campaignId: number
  scheduleTimezone: string
  initialScheduledAt?: string | null
}

/**
 * Compute the offset (in minutes) of `timeZone` at the given UTC instant.
 * Positive when the zone is behind UTC (e.g. -300 for America/New_York in EST).
 */
function getZoneOffsetMinutes(timeZone: string, utcMs: number): number {
  // `hourCycle: 'h23'` forces 0-23 -- `hour12: false` historically emits 24
  // at midnight in some engines, which would silently roll the day forward.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs))
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value)
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  )
  return (asUtc - utcMs) / 60_000
}

function zonedDateTimeToUtcIso(
  dateStr: string,
  timeStr: string,
  timeZone: string
): string {
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hour, minute] = timeStr.split(':').map(Number)
  // Treat the wall-clock in `timeZone`. First pass: assume local == UTC.
  const guessUtcMs = Date.UTC(year, month - 1, day, hour, minute)
  const offset = getZoneOffsetMinutes(timeZone, guessUtcMs)
  return new Date(guessUtcMs - offset * 60_000).toISOString()
}

function splitZonedIso(
  iso: string,
  timeZone: string
): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  }
}

export function ScheduleLaunchModal({
  show,
  onHide,
  bisonCampaignId,
  campaignId,
  scheduleTimezone,
  initialScheduledAt,
}: Props) {
  // Seed `time` with the same default the TimePicker shows ("09:00 AM") so
  // canSubmit reflects the visible state. Previously we relied on the
  // picker pushing its default upward on mount, but that round-trip left
  // the Schedule button disabled until the user reselected a time.
  const initial = useMemo(
    () =>
      initialScheduledAt
        ? splitZonedIso(initialScheduledAt, scheduleTimezone)
        : { date: '', time: '09:00' },
    [initialScheduledAt, scheduleTimezone]
  )

  const [date, setDate] = useState<string>(initial.date)
  const [time, setTime] = useState<string>(initial.time)

  const scheduleMutation = useScheduleCampaignLaunch(
    bisonCampaignId,
    campaignId
  )

  const scheduledIso = useMemo(() => {
    if (!date || !time) return null
    try {
      return zonedDateTimeToUtcIso(date, time, scheduleTimezone)
    } catch {
      return null
    }
  }, [date, time, scheduleTimezone])

  const isInFuture = useMemo(() => {
    if (!scheduledIso) return false
    return new Date(scheduledIso).getTime() > Date.now()
  }, [scheduledIso])

  const canSubmit = Boolean(date && time && isInFuture)

  async function handleSubmit() {
    if (!scheduledIso) return
    try {
      await scheduleMutation.mutateAsync({ scheduled_launch_at: scheduledIso })
      toast.success('Campaign scheduled for launch')
      onHide()
    } catch (err: unknown) {
      toast.error(getApiErrorDetail(err, 'Failed to schedule campaign launch'))
    }
  }

  return (
    <Modal
      open={show}
      onOpenChange={(open: boolean) => !open && onHide()}
      title="Schedule launch"
      description="Pick a date and time. Your campaign will launch automatically at that moment."
      onAccept={handleSubmit}
      acceptButtonText="Schedule"
      blockAccept={!canSubmit}
      loading={scheduleMutation.isPending}
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-fg">Date</label>
          <DatePicker
            value={date}
            onChange={(v) => setDate(v ?? '')}
            allowTyping
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-fg">Time</label>
          <TimePicker value={time} onChange={setTime} />
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-surface-alt p-3">
          <Clock className="mt-0.5 size-4 shrink-0 text-fg-secondary" />
          <p className="text-xs text-fg-secondary">
            Times shown in <strong>{scheduleTimezone}</strong> (your send
            schedule).
          </p>
        </div>
        {date && time && !isInFuture && (
          <p className="text-xs text-red-500">
            Scheduled time must be in the future.
          </p>
        )}
      </div>
    </Modal>
  )
}
